import React, { useRef, useEffect, useContext, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

// Components
import { ConversationWindowStyled, ConversationContainer, MessagesContainerStyled } from './ConversationWindowStyled';
import CurrentConversationHeader from './CurrentConversationHeader';
import ConversationMessage from './ConversationMessage';
import LoadingArea from '@molecules/LoadingArea';
import LoadingInline from '@molecules/LoadingInline';

// Functionality
import { ConversationsContext } from '../../../context/ConversationsProvider';
import useCurrentParticipantMember from './hooks/useCurrentParticipantMember';
import useConversationMessages from './hooks/useConversationMessages';
import { InboxReadyContext } from './providers/InboxReadyProvider';
import useViewer from '@hooks/useViewer';

const ConversationWindow = () => {
    const [displayedMessages, setDisplayedMessages] = useState();
    const [currentPageData, setCurrentPageData] = useState();
    const [isReady, setIsReady] = useState(false);
    const { viewer } = useViewer();
    const { conversations, currentConversation } = useContext(ConversationsContext);
    const { currentParticipantMember } = useCurrentParticipantMember(currentConversation);
    const { firstPageMessageData } = useConversationMessages(currentConversation);
    const { setIsInboxReady } = useContext(InboxReadyContext);

    const messagesEndRef = useRef(null);
    const containerRef = useRef(null);
    const scrollToBottom = () => {
        // TODO: only scroll if current conversation - how to figure this out?
        if (!containerRef.current) return;
        // console.log('scrolling');

        containerRef.current?.scroll(0, containerRef.current?.scrollHeight);
    };

    useEffect(() => {
        // console.log({ currentConversation, currentParticipantMember, displayedMessages, viewer });
        if (!currentConversation || !currentParticipantMember || !displayedMessages || !viewer) {
            setIsReady(false);
        } else {
            setIsReady(true);
        }
    }, [currentConversation, currentParticipantMember, displayedMessages, viewer]);

    useEffect(() => {
        if (conversations.length === 0 || isReady) {
            setIsInboxReady(true);
        }
    }, [conversations, isReady, setIsInboxReady]);

    useEffect(() => {
        // TODO: how to cancel this? shows memory leak error when sending message from modal
        scrollToBottom();
    }, [isReady]);

    // when the current conversation changes, reset it so there is no overlap
    useEffect(() => {
        setCurrentPageData();
        setDisplayedMessages();
    }, [currentConversation]);

    useEffect(() => {
        if (!firstPageMessageData?.items) return;
        setCurrentPageData(firstPageMessageData);
        setDisplayedMessages(firstPageMessageData.items.reverse());
        // sometimes this works right away, other times not. Not sure why.
        scrollToBottom();
        setTimeout(() => {
            scrollToBottom();
        }, 100);
    }, [firstPageMessageData]);

    const fetchMoreMessages = async () => {
        const more = await currentPageData.prevPage();
        setCurrentPageData(more);
        console.log({ more });
        setDisplayedMessages([...displayedMessages, ...more.items.reverse()]);
    };

    if (conversations.length === 0) {
        return (
            <ConversationWindowStyled>
                <p className="font-size-sm">Your messages will appear here.</p>
            </ConversationWindowStyled>
        );
    }

    if (!isReady) return <LoadingArea minHeight={517} />;

    return (
        <>
            <div className="fade-in-fast" style={{ height: '507px' }}>
                <CurrentConversationHeader />
                <ConversationWindowStyled className="fade-in-fast" id="conversation-scrolled-window" ref={containerRef}>
                    <ConversationContainer>
                        {displayedMessages?.length > 0 && (
                            <MessagesContainerStyled>
                                <InfiniteScroll
                                    dataLength={displayedMessages.length}
                                    next={fetchMoreMessages}
                                    hasMore={currentPageData.hasPrevPage}
                                    loader={<LoadingInline />}
                                    inverse
                                    style={{ display: 'flex', flexDirection: 'column-reverse' }}
                                    scrollableTarget="conversation-scrolled-window"
                                    endMessage={
                                        <p
                                            style={{
                                                marginBottom: '10px',
                                                lineHeight: '1.2rem',
                                                fontSize: '0.9rem',
                                                color: '#5F5F5F',
                                                textAlign: 'center',
                                            }}
                                        >
                                            Start of conversation
                                        </p>
                                    }
                                >
                                    {displayedMessages.map((message, index) => (
                                        <ConversationMessage
                                            key={`message-${message.sid}}`}
                                            message={message}
                                            currentParticipantMember={currentParticipantMember}
                                            viewerID={viewer?.id}
                                        />
                                    ))}
                                </InfiniteScroll>
                            </MessagesContainerStyled>
                        )}
                    </ConversationContainer>
                    <div ref={messagesEndRef} />
                </ConversationWindowStyled>
            </div>
        </>
    );
};

ConversationWindow.propTypes = {};

export default ConversationWindow;
